import { FilePreview } from '@components/Files/FilePreview';
import { useUploadContext } from '@components/Files/UploadProvider';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { Select } from '@components/core-ui/form-elements/Select';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useState } from 'react';
import sanitize from 'sanitize-filename';
import { BulkJobStep, useCurrentModalContext } from '../../providers/ModalProvider';

export const CreateJobsStep = () => {
  const { step, isLoading, isFormDisabled, error, layouts, onCancel, createJobs } = useCurrentModalContext();
  const { files } = useUploadContext();
  const [layoutIds, setLayoutIds] = useState<(string | null)[]>([]);
  const [jobNames, setJobNames] = useState<(string | null)[]>([]);

  if (step !== BulkJobStep.CreateJobs) {
    return null;
  }

  const layoutsList = [
    {
      label: 'None',
      value: 'null',
    },
    ...(layouts || []).map((l) => ({
      value: l.id,
      label: l.name,
    })),
  ];

  const setLayout = (index: number, value: string) => {
    const copy = [...layoutIds];
    copy[index] = value;
    setLayoutIds(copy);
  };

  const setJobName = (index: number, value: string) => {
    const copy = [...jobNames];
    copy[index] = value;
    setJobNames(copy);
  };

  const submit = async () => {
    const jobs = files.map((file, index) => {
      const sanitizedName = sanitize(jobNames[index] || file.meta.name || '')
        .replace(/[^a-zA-Z0-9()_.\-\s]/gi, '-')
        .substring(0, 200)
        .trim();
      return {
        id: file.meta.extra?.jobId,
        file,
        name: sanitizedName,
        layoutId: layoutIds[index] || undefined,
      };
    });
    await createJobs(jobs);
  };

  return (
    <div className="step-wrapper">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <div className="previews">
        {files.map((item, index) => {
          const sanitizedFileName = sanitize(
            (item.meta.name || '')
              .replace(/[^a-zA-Z0-9()_.\-\s]/gi, '-')
              .substring(0, 200)
              .trim(),
          );
          return (
            <div key={index} className="file-wrapper">
              <FilePreview
                item={item}
                bottomSlot={
                  <>
                    <div className="w-full input-wrapper">
                      <label>Job Name:</label>
                      <Input
                        name="name"
                        className="job-name-input"
                        label="Job Name"
                        value={jobNames[index] || sanitizedFileName}
                        disabled={isLoading}
                        onChange={(e) => setJobName(index, e.target.value)}
                        required={false}
                      />
                    </div>
                    <div className="w-full input-wrapper">
                      <label>Cloud Layout:</label>
                      <Select
                        name="layoutId"
                        className="layout-select"
                        label="Cloud Layout"
                        value={layoutIds[index] || 'null'}
                        disabled={layoutsList.length < 2 || isLoading}
                        onChange={(_, value) => setLayout(index, value)}
                        options={layoutsList}
                        required={false}
                      />
                    </div>
                  </>
                }
              />
            </div>
          );
        })}
      </div>
      {error ? <div className="errorMessage">{error}</div> : null}
      <div className="buttonContainer">
        <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
        <Button className="ml-4" onClick={submit} disabled={isLoading || !files.length || isFormDisabled}>
          Create Jobs
        </Button>
      </div>
    </div>
  );
};
